import { Input } from "@ember/component";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import ConditionalLoadingSpinner from "discourse/components/conditional-loading-spinner";
import DButton from "discourse/components/d-button";
import LoadMore from "discourse/components/load-more";
import PluginOutlet from "discourse/components/plugin-outlet";
import ResponsiveTable from "discourse/components/responsive-table";
import TableHeaderToggle from "discourse/components/table-header-toggle";
import TextField from "discourse/components/text-field";
import UserInfo from "discourse/components/user-info";
import icon from "discourse/helpers/d-icon";
import hideApplicationFooter from "discourse/helpers/hide-application-footer";
import lazyHash from "discourse/helpers/lazy-hash";
import routeAction from "discourse/helpers/route-action";
import { or } from "discourse/truth-helpers";
import dAgeWithTooltip from "discourse/ui-kit/helpers/d-age-with-tooltip";
import { i18n } from "discourse-i18n";
import CircleMembersDropdownActions from "../../components/circles/members-dropdown-actions";
import CircleMembersDropdownBulkActions from "../../components/circles/members-dropdown-bulk-actions";

const CircleMembers = <template>
  {{#if (or this.loading this.canLoadMore)}}
    {{hideApplicationFooter}}
  {{/if}}

  <section class="circles-members-wrapper circles-tab-content">
    <div class="group-members-actions circles-members-actions">
      {{#if @controller.canManageGroup}}
        <DButton
          @icon="list"
          @action={{@controller.toggleBulkSelect}}
          @title="topics.bulk.toggle"
          class="bulk-select"
        />
      {{/if}}

      {{#if @model.can_see_members}}
        <TextField
          @value={{@controller.filterInput}}
          @placeholderKey={{@controller.filterPlaceholder}}
          @autocomplete="off"
          class="circles-username-filter no-blur"
        />
      {{/if}}

      {{#if @controller.canManageGroup}}
        {{#if @controller.isBulk}}
          <span class="bulk-select-buttons-wrap">
            {{#if @controller.bulkSelection}}
              <CircleMembersDropdownBulkActions
                @bulkSelection={{@controller.bulkSelection}}
                @canAdminGroup={{@model.can_admin_group}}
                @canEditGroup={{@model.can_edit_group}}
                @onChange={{fn
                  @controller.actOnSelection
                  @controller.bulkSelection
                }}
              />

              <DButton
                @action={{@controller.bulkClearAll}}
                @label="topics.bulk.clear_all"
                @icon="far-square"
                class="bulk-select-clear"
              />
            {{/if}}

            <DButton
              @action={{@controller.bulkSelectAll}}
              @label="topics.bulk.select_all"
              @icon="square-check"
              class="bulk-select-all"
            />
          </span>
        {{/if}}

        <div class="circles-members-manage">
          <DButton
            @icon="plus"
            @action={{routeAction "showAddMembersModal"}}
            @label="discourse_circles.circle.manage.add_members"
            class="btn-default circles-members-add"
          />
        </div>
      {{/if}}
    </div>

    {{#if @controller.hasMembers}}
      <PluginOutlet
        @name="circle-members-list"
        @outletArgs={{lazyHash
          loadMore=@controller.loadMore
          canManageGroup=@controller.canManageGroup
          members=@model.members
          removeMember=@controller.removeMember
        }}
      >
        <LoadMore
          @selector=".directory-table .directory-table__cell"
          @action={{@controller.loadMore}}
        >
          <ResponsiveTable
            @className="group-members circles-members
              {{if @controller.isBulk 'sticky-header' ''}}
              {{if @controller.canManageGroup 'group-members--can-manage' ''}}
            "
          >
            <:header>
              <TableHeaderToggle
                @order={{@controller.order}}
                @asc={{@controller.asc}}
                @field="username_lower"
                @labelKey="username"
                @automatic={{true}}
                @colspan="2"
                class="directory-table__column-header--username username"
              />

              {{#if @controller.canManageGroup}}
                <div
                  class="directory-table__column-header directory-table__column-header--can-manage"
                ></div>
              {{/if}}

              <TableHeaderToggle
                @order={{@controller.order}}
                @asc={{@controller.asc}}
                @field="added_at"
                @labelKey="groups.member_added"
                @automatic={{true}}
                class="directory-table__column-header--added"
              />
              <TableHeaderToggle
                @order={{@controller.order}}
                @asc={{@controller.asc}}
                @field="last_posted_at"
                @labelKey="last_post"
                @automatic={{true}}
                class="directory-table__column-header--last-posted"
              />
              <TableHeaderToggle
                @order={{@controller.order}}
                @asc={{@controller.asc}}
                @field="last_seen_at"
                @labelKey="last_seen"
                @automatic={{true}}
                class="directory-table__column-header--last-seen"
              />

              {{#if @controller.canManageGroup}}
                <div
                  class="directory-table__column-header directory-table__column-header--member-settings"
                ></div>
              {{/if}}
            </:header>

            <:body>
              {{#each @model.members as |m|}}
                <div
                  class="directory-table__row"
                  data-user-id={{m.id}}
                  data-username={{m.username}}
                >
                  <div
                    colspan="2"
                    class="directory-table__cell directory-table__cell--username group-member circles-member"
                  >
                    {{#if @controller.canManageGroup}}
                      {{#if @controller.isBulk}}
                        <Input
                          {{on "click" (fn @controller.selectMember m)}}
                          @type="checkbox"
                          class="bulk-select"
                        />
                      {{/if}}
                    {{/if}}

                    <UserInfo
                      @user={{m}}
                      @skipName={{@controller.skipName}}
                      @showStatus={{true}}
                      @showStatusTooltip={{true}}
                    />
                  </div>

                  {{#if @controller.canManageGroup}}
                    <div
                      class="directory-table__cell directory-table__cell--can-manage circles-member__badges"
                    >
                      {{#if (or m.owner m.primary)}}
                        <span class="directory-table__label">
                          <span>{{i18n "groups.members.status"}}</span>
                        </span>
                      {{/if}}

                      {{#if m.owner}}
                        <span
                          class="directory-table__value circles-member__owner-badge"
                        >
                          {{icon "shield-halved"}}
                          {{i18n "groups.members.owner"}}<br />
                        </span>
                      {{/if}}
                      {{#if m.primary}}
                        <span
                          class="directory-table__value circles-member__primary-badge"
                        >
                          {{i18n "groups.members.primary"}}
                        </span>
                      {{/if}}
                    </div>
                  {{/if}}

                  <div
                    class="directory-table__cell directory-table__cell--added"
                  >
                    <span class="directory-table__label">
                      <span>{{i18n "groups.member_added"}}</span>
                    </span>
                    <span class="directory-table__value">
                      {{dAgeWithTooltip m.added_at format="medium"}}
                    </span>
                  </div>

                  <div
                    class="directory-table__cell{{unless
                        m.last_posted_at
                        '--empty'
                      }}
                      directory-table__cell--last-posted"
                  >
                    {{#if m.last_posted_at}}
                      <span class="directory-table__label">
                        <span>{{i18n "last_post"}}</span>
                      </span>
                    {{/if}}
                    <span class="directory-table__value">
                      {{dAgeWithTooltip m.last_posted_at format="medium"}}
                    </span>
                  </div>

                  <div
                    class="directory-table__cell{{unless
                        m.last_seen_at
                        '--empty'
                      }}
                      directory-table__cell--last-seen"
                  >
                    {{#if m.last_seen_at}}
                      <span class="directory-table__label">
                        <span>{{i18n "last_seen"}}</span>
                      </span>
                    {{/if}}
                    <span class="directory-table__value">
                      {{dAgeWithTooltip m.last_seen_at format="medium"}}
                    </span>
                  </div>

                  {{#if @controller.canManageGroup}}
                    <div
                      class="directory-table__cell directory-table__cell--member-settings member-settings"
                    >
                      <CircleMembersDropdownActions
                        @member={{m}}
                        @canAdminGroup={{@model.can_admin_group}}
                        @canEditGroup={{@model.can_edit_group}}
                        @onChange={{fn @controller.actOnGroup m}}
                      />
                    </div>
                  {{/if}}
                </div>
              {{/each}}
            </:body>
          </ResponsiveTable>
        </LoadMore>
      </PluginOutlet>

      <ConditionalLoadingSpinner @condition={{@controller.loading}} />
    {{else}}
      <br />

      <div>{{i18n @controller.emptyMessageKey}}</div>
    {{/if}}
  </section>
</template>;

export default CircleMembers;
